import { useState, useCallback, useRef, useEffect } from "react";

type Props = {
  id: string;
  imageUrl: string;
  caption: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  isSelected: boolean;
  canEdit: boolean;
  zoom: number;
  onSelect: () => void;
  onUpdate: (updates: { x?: number; y?: number; width?: number; height?: number; caption?: string }) => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onDoubleClick?: () => void;
};

type ResizeHandle = "nw" | "ne" | "sw" | "se";

export function DraggableImage({
  id,
  imageUrl,
  caption,
  x,
  y,
  width,
  height,
  zIndex,
  isSelected,
  canEdit,
  zoom,
  onSelect,
  onUpdate,
  onDelete,
  onBringToFront,
  onDoubleClick,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<ResizeHandle | null>(null);
  const [dragStart, setDragStart] = useState({ mouseX: 0, mouseY: 0, elemX: 0, elemY: 0 });
  const [resizeStart, setResizeStart] = useState({ mouseX: 0, mouseY: 0, width: 0, height: 0, x: 0, y: 0 });
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [editedCaption, setEditedCaption] = useState(caption || "");
  const [isHovered, setIsHovered] = useState(false);

  const saveTimeoutRef = useRef<number | null>(null);

  // Debounced save
  const debouncedSave = useCallback((updates: { x?: number; y?: number; width?: number; height?: number }) => {
    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = window.setTimeout(() => {
      onUpdate(updates);
    }, 300);
  }, [onUpdate]);

  // Drag handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!canEdit) return;
    if ((e.target as HTMLElement).closest('.resize-handle, button, input, textarea')) return;

    e.preventDefault();
    e.stopPropagation();
    onSelect();
    onBringToFront();
    setIsDragging(true);
    setDragStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      elemX: x,
      elemY: y,
    });
  }, [canEdit, x, y, onSelect, onBringToFront]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = (e.clientX - dragStart.mouseX) / zoom;
      const dy = (e.clientY - dragStart.mouseY) / zoom;
      const newX = Math.max(0, dragStart.elemX + dx);
      const newY = Math.max(0, dragStart.elemY + dy);

      // Update local position immediately for smooth dragging
      if (containerRef.current) {
        containerRef.current.style.transform = `translate(${newX}px, ${newY}px)`;
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      const dx = (e.clientX - dragStart.mouseX) / zoom;
      const dy = (e.clientY - dragStart.mouseY) / zoom;
      const newX = Math.max(0, Math.round(dragStart.elemX + dx));
      const newY = Math.max(0, Math.round(dragStart.elemY + dy));
      onUpdate({ x: newX, y: newY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, zoom, onUpdate]);

  // Resize handling
  const handleResizeStart = useCallback((e: React.MouseEvent, handle: ResizeHandle) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    setIsResizing(handle);
    setResizeStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      width,
      height,
      x,
      y,
    });
  }, [canEdit, width, height, x, y, onSelect]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = (e.clientX - resizeStart.mouseX) / zoom;
      const dy = (e.clientY - resizeStart.mouseY) / zoom;

      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.x;
      let newY = resizeStart.y;

      // Maintain aspect ratio
      const aspectRatio = resizeStart.width / resizeStart.height;

      switch (isResizing) {
        case "se":
          newWidth = Math.max(80, resizeStart.width + dx);
          newHeight = newWidth / aspectRatio;
          break;
        case "sw":
          newWidth = Math.max(80, resizeStart.width - dx);
          newHeight = newWidth / aspectRatio;
          newX = resizeStart.x + (resizeStart.width - newWidth);
          break;
        case "ne":
          newWidth = Math.max(80, resizeStart.width + dx);
          newHeight = newWidth / aspectRatio;
          newY = resizeStart.y + (resizeStart.height - newHeight);
          break;
        case "nw":
          newWidth = Math.max(80, resizeStart.width - dx);
          newHeight = newWidth / aspectRatio;
          newX = resizeStart.x + (resizeStart.width - newWidth);
          newY = resizeStart.y + (resizeStart.height - newHeight);
          break;
      }

      if (containerRef.current) {
        containerRef.current.style.transform = `translate(${newX}px, ${newY}px)`;
        containerRef.current.style.width = `${newWidth}px`;
        const imgContainer = containerRef.current.querySelector('.img-container') as HTMLElement;
        if (imgContainer) {
          imgContainer.style.height = `${newHeight}px`;
        }
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      const dx = (e.clientX - resizeStart.mouseX) / zoom;
      const dy = (e.clientY - resizeStart.mouseY) / zoom;

      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.x;
      let newY = resizeStart.y;

      const aspectRatio = resizeStart.width / resizeStart.height;

      switch (isResizing) {
        case "se":
          newWidth = Math.max(80, resizeStart.width + dx);
          newHeight = newWidth / aspectRatio;
          break;
        case "sw":
          newWidth = Math.max(80, resizeStart.width - dx);
          newHeight = newWidth / aspectRatio;
          newX = resizeStart.x + (resizeStart.width - newWidth);
          break;
        case "ne":
          newWidth = Math.max(80, resizeStart.width + dx);
          newHeight = newWidth / aspectRatio;
          newY = resizeStart.y + (resizeStart.height - newHeight);
          break;
        case "nw":
          newWidth = Math.max(80, resizeStart.width - dx);
          newHeight = newWidth / aspectRatio;
          newX = resizeStart.x + (resizeStart.width - newWidth);
          newY = resizeStart.y + (resizeStart.height - newHeight);
          break;
      }

      setIsResizing(null);
      onUpdate({
        x: Math.round(newX),
        y: Math.round(newY),
        width: Math.round(newWidth),
        height: Math.round(newHeight),
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, zoom, onUpdate]);

  const handleCaptionSave = useCallback(() => {
    setIsEditingCaption(false);
    if (editedCaption !== caption) {
      onUpdate({ caption: editedCaption });
    }
  }, [editedCaption, caption, onUpdate]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDoubleClick?.();
  }, [onDoubleClick]);

  return (
    <div
      ref={containerRef}
      className={`absolute group draggable-item ${isDragging || isResizing ? 'cursor-grabbing' : canEdit ? 'cursor-grab' : ''}`}
      style={{
        transform: `translate(${x}px, ${y}px)`,
        width,
        zIndex,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image container */}
      <div
        className={`img-container relative bg-background border rounded-lg overflow-hidden transition-shadow ${
          isSelected ? 'ring-2 ring-primary-500 border-primary-500' : 'border-border'
        }`}
        style={{ height }}
      >
        <img
          src={imageUrl}
          alt={caption || "Storyboard image"}
          className="w-full h-full object-cover"
          draggable={false}
        />

        {/* Delete button */}
        {canEdit && isHovered && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm("Delete this image?")) {
                onDelete();
              }
            }}
            className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-red-500 rounded-full transition-colors"
          >
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Resize handles - only show when selected and can edit */}
        {canEdit && isSelected && (
          <>
            <div
              className="resize-handle absolute -top-1 -left-1 w-3 h-3 bg-primary-500 rounded-full cursor-nw-resize"
              onMouseDown={(e) => handleResizeStart(e, "nw")}
            />
            <div
              className="resize-handle absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full cursor-ne-resize"
              onMouseDown={(e) => handleResizeStart(e, "ne")}
            />
            <div
              className="resize-handle absolute -bottom-1 -left-1 w-3 h-3 bg-primary-500 rounded-full cursor-sw-resize"
              onMouseDown={(e) => handleResizeStart(e, "sw")}
            />
            <div
              className="resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-primary-500 rounded-full cursor-se-resize"
              onMouseDown={(e) => handleResizeStart(e, "se")}
            />
          </>
        )}
      </div>

      {/* Caption */}
      <div className="mt-1 px-1">
        {isEditingCaption && canEdit ? (
          <input
            type="text"
            value={editedCaption}
            onChange={(e) => setEditedCaption(e.target.value)}
            onBlur={handleCaptionSave}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCaptionSave();
              if (e.key === "Escape") {
                setEditedCaption(caption || "");
                setIsEditingCaption(false);
              }
            }}
            autoFocus
            className="w-full bg-transparent border-b border-primary-500 text-xs text-muted focus:outline-none focus:text-foreground"
            placeholder="Add caption..."
          />
        ) : (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (canEdit) {
                setIsEditingCaption(true);
              }
            }}
            className={`text-xs whitespace-normal break-words ${caption ? 'text-muted' : 'text-muted/50 italic'} ${canEdit ? 'cursor-text hover:text-foreground' : ''}`}
          >
            {caption || (canEdit ? "Add caption..." : "")}
          </div>
        )}
      </div>
    </div>
  );
}
